import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSWRConfig } from 'swr'
import { toast } from 'sonner'
import { Check, FolderPlus, Loader2, Plus } from '@digital-twins/design-system/icons'
import { LibraryCollectionCard, LibraryItemRef, canAddLibraryCollectionMembers } from '@digital-twins/shared-types'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { libraryCollectionsApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { revalidateLibraryCollection } from '@/pages/KnowledgeCollectionPage/useLibraryCollection'
import { useLibraryCollectionsList } from '@/pages/KnowledgeCollectionsListPage/useLibraryCollectionsList'
import { useItemCollections } from '@/pages/KnowledgeHubPage/collections/useItemCollections'

import { CollectionCreateDialog } from './CollectionCreateDialog'

export interface AddToCollectionMenuProps {
  /** One item: membership marks, check includes, uncheck excludes. Several: add-only targets. */
  items: Array<LibraryItemRef>
  /** The trigger; defaults to an "Add to collection" button. */
  trigger?: ReactNode
  testId: string
  /** Several items: called after the POSTs with the items that failed (empty on full success). */
  onBulkDone?: (failed: Array<LibraryItemRef>) => void
  /**
   * Read an audience's collections only while the menu is open. For lists
   * that already show the menu on the org's own audiences alone (D15), so a
   * grid does not send one lookup per card.
   */
  readsMembershipOnOpen?: boolean
}

/**
 * POSTs `include` for each item into one collection, in parallel, and
 * returns the items whose POST failed. Exported for tests.
 */
export async function addItemsToCollection(
  collectionToken: string,
  items: Array<LibraryItemRef>,
): Promise<Array<LibraryItemRef>> {
  const results = await Promise.allSettled(
    items.map((item) =>
      libraryCollectionsApi.addMember(collectionToken, { kind: item.kind, token: item.token, mode: 'include' }),
    ),
  )
  return items.filter((_, index) => results[index].status === 'rejected')
}

/**
 * The one "Add to collection" target picker, for one item and for many.
 * Targets are the viewer's curated collections with `canEdit` (paged
 * `GET /library/collections`, read only while the menu is open). With one
 * item, marks come from `GET /library/items/:kind/:token/collections`:
 * checking POSTs `include`, unchecking always POSTs `exclude` (for a manual
 * and a rule member alike, so a rule match cannot bring the item back), and
 * a 404 lookup (an audience that is not the org's own, D15) renders nothing.
 * The lookup is read only while the menu is open (a Library home mounts one
 * menu per card), except for an audience, whose 404 must hide the trigger.
 * With several items the targets are add-only, then a toast and
 * `onBulkDone(failed)`. "New collection" opens the create dialog with the
 * items as `includes`.
 */
export function AddToCollectionMenu(props: AddToCollectionMenuProps) {
  const { items, trigger, testId, onBulkDone, readsMembershipOnOpen = false } = props

  const { t } = useTranslation('app')
  const [isOpen, setIsOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [pendingToken, setPendingToken] = useState<string | null>(null)

  const isSingle = items.length === 1
  const single = isSingle ? items[0] : null
  const { mutate: mutateScoped } = useSWRConfig()
  // Only an audience reads eagerly: a 404 (not the org's own, D15) hides the whole menu.
  const readsEagerly = single?.kind === 'audience' && !readsMembershipOnOpen
  const itemCollections = useItemCollections(isOpen || readsEagerly ? single : null)
  const list = useLibraryCollectionsList(isOpen)

  // A saved filter holds rules only (D1), so it is never a target.
  const targets = list.collections.filter(canAddLibraryCollectionMembers)
  const membershipOf = new Map(itemCollections.collections.map((collection) => [collection.token, collection.membership]))

  const onToggle = async (collection: LibraryCollectionCard) => {
    const isMember = membershipOf.has(collection.token)
    setPendingToken(collection.token)
    try {
      await itemCollections.setMembership(collection.token, isMember ? 'exclude' : 'include')
      toast.success(
        t(isMember ? 'library.collections.menu.removed' : 'library.collections.menu.added', {
          name: collection.name,
        }),
      )
    } catch {
      toast.error(t('library.collections.menu.updateFailed'))
    } finally {
      setPendingToken(null)
    }
  }

  const onBulkAdd = async (collection: LibraryCollectionCard) => {
    setPendingToken(collection.token)
    try {
      const failed = await addItemsToCollection(collection.token, items)
      const added = items.length - failed.length
      if (added > 0) {
        toast.success(t('library.collections.menu.bulkAdded', { count: added, name: collection.name }))
      }
      if (failed.length > 0) {
        toast.error(t('library.collections.menu.bulkFailed', { count: failed.length }))
      }
      await revalidateLibraryCollection(mutateScoped, collection.token, items)
      onBulkDone?.(failed)
      setIsOpen(false)
    } finally {
      setPendingToken(null)
    }
  }

  const onNewCollection = () => {
    setIsOpen(false)
    setIsCreating(true)
  }

  if (isSingle && itemCollections.isNotFound) {
    return null
  }
  if (items.length === 0) {
    return null
  }

  const triggerNode = trigger ?? (
    <Button type="button" variant="outline" size="sm" className="gap-1.5" data-testid={`${testId}-trigger`}>
      <FolderPlus className="h-4 w-4" aria-hidden="true" />
      {t('library.collections.addToCollection')}
    </Button>
  )

  const isBusy = pendingToken !== null

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        {/* asChild: the trigger is the caller's own control (an icon on a card overlay, a header button). */}
        <PopoverTrigger asChild>{triggerNode}</PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-72 rounded-[calc(var(--radius)+8px)] border-card-border bg-background p-1.5"
          data-testid={testId}
          onClick={(event) => event.stopPropagation()}
        >
          <div
            role="menu"
            aria-label={t('library.collections.addToCollection')}
            aria-busy={isBusy || list.isLoading}
            className="flex max-h-80 flex-col overflow-y-auto"
          >
            {list.isLoading || (isSingle && itemCollections.isLoading) ? (
              <div className="flex justify-center py-3" data-testid={`${testId}-loading`}>
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
              </div>
            ) : (
              targets.map((collection, index) => {
                const membership = membershipOf.get(collection.token) ?? null
                const isPending = pendingToken === collection.token
                const rowTestId = `${testId}-target-${index}`
                const rowClass =
                  'flex min-w-0 items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors duration-150 hover:bg-accent focus-visible:bg-accent focus-visible:outline-none disabled:opacity-60'
                if (isSingle) {
                  return (
                    <button
                      key={collection.token}
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={membership !== null}
                      className={rowClass}
                      disabled={isBusy}
                      data-testid={rowTestId}
                      data-collection-token={collection.token}
                      onClick={() => void onToggle(collection)}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                          membership ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
                        )}
                        aria-hidden="true"
                      >
                        {isPending ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : membership ? (
                          <Check className="h-3 w-3" />
                        ) : null}
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-foreground">{collection.name}</span>
                        {membership === 'rule' ? (
                          <span className="text-xs text-muted-foreground" data-testid={`${rowTestId}-rule`}>
                            {t('library.collections.menu.linkedByRule')}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  )
                }
                return (
                  <button
                    key={collection.token}
                    type="button"
                    role="menuitem"
                    className={rowClass}
                    disabled={isBusy}
                    data-testid={rowTestId}
                    data-collection-token={collection.token}
                    onClick={() => void onBulkAdd(collection)}
                  >
                    {isPending ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden="true" />
                    ) : null}
                    <span className="min-w-0 flex-1 truncate text-foreground">{collection.name}</span>
                  </button>
                )
              })
            )}
            {list.hasMore ? (
              <button
                type="button"
                className="rounded-lg px-2.5 py-2 text-left text-sm text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:bg-accent focus-visible:outline-none"
                disabled={list.isLoadingMore}
                data-testid={`${testId}-more`}
                onClick={list.loadMore}
              >
                {t('library.collections.showMore')}
              </button>
            ) : null}
            {targets.length > 0 ? <div className="my-1 h-px bg-card-border" role="separator" /> : null}
            <button
              type="button"
              role="menuitem"
              className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-foreground hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
              disabled={isBusy}
              data-testid={`${testId}-new`}
              onClick={onNewCollection}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('library.collections.newCollection')}
            </button>
          </div>
        </PopoverContent>
      </Popover>
      <CollectionCreateDialog
        open={isCreating}
        onOpenChange={setIsCreating}
        mode="create"
        entry={isSingle ? 'item_menu' : 'multiselect'}
        initialIncludes={items}
        onSaved={() => {
          if (!isSingle) {
            onBulkDone?.([])
          }
          void itemCollections.refresh()
        }}
      />
    </>
  )
}
